import React, { useMemo, useState } from 'react';
import WizardShell from '../ui/wizard-shell';
import type { TrialLessonDTO } from '../../lib/trials';
import { type MessageDraft, resendTrialConfirmation } from '../../lib/trials';
import { toast } from 'sonner';
import CommunicationComposer, { type RecipientGroup } from '../communications/CommunicationComposer';
import CommunicationStandortHint from '../communications/CommunicationStandortHint';
import { WizardCard, WizardSection, WizardKVGrid, WizardKVItem, WizardYesNoBadge } from '../ui/wizard-layout';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trial: TrialLessonDTO;
  standortId?: number;
  onUpdated?: (trial: TrialLessonDTO) => void;
};
type StepState = 1 | 2 | 3;
type StepDelta = -1 | 1;

export default function ResendTrialConfirmationWizardModal({ open, onOpenChange, trial, standortId, onUpdated }: Readonly<Props>) {
  const [step, setStep] = useState<StepState>(1);
  // Der bearbeitete Text aus dem Editor. Bisher ging stattdessen ein fester Satz raus (T-0067).
  const [entwurf, setEntwurf] = useState<MessageDraft | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const moveStep = (delta: StepDelta) => {
    setStep((prev) => {
      const next = (prev + delta) as StepState;
      return Math.min(3, Math.max(1, next)) as StepState;
    });
  };

  const [sendEmail, setSendEmail] = useState(true);
  const [sendSms, setSendSms] = useState(false);

  const canSubmit = sendEmail || sendSms;

  const composerGroups: RecipientGroup[] = useMemo(() => {
    const sp = String((trial as any)?.subprofileId ?? (trial as any)?.subprofile_id ?? '').trim();
    if (!sp) return [];
    let wpUserId = 0;
    let subIndex: number | null = null;
    if (sp.includes('-')) {
      const [a, b] = sp.split('-', 2);
      wpUserId = Number.parseInt(a, 10) || 0;
      subIndex = b ? (Number.parseInt(b, 10) || null) : null;
    } else {
      wpUserId = Number.parseInt(sp, 10) || 0;
    }
    if (!wpUserId) return [];
    return [{
      key: 'student',
      label: 'Teilnehmer',
      recipients: [{ id: `person:${wpUserId}:${subIndex ?? ''}`, type: 'person' as const, wpUserId, subIndex, name: trial.studentName || 'Teilnehmer' }],
      templateKeys: { email: 'trial.confirmation.resend.student', sms: 'trial.confirmation.resend.student' },
      vars: {
        studentName: trial.studentName || '',
        teacherName: (trial as any)?.teacherName || '',
        date: (trial as any)?.date || (trial as any)?.start_datum || '',
        startTime: (trial as any)?.startTime || (trial as any)?.start_time || '',
        endTime: (trial as any)?.endTime || (trial as any)?.end_time || '',
        roomName: (trial as any)?.roomName || (trial as any)?.raum_name || '',
        roomClause: (trial as any)?.roomName ? ` im Raum ${(trial as any)?.roomName}` : '',
        fachName: (trial as any)?.fachName || '',
      },
      defaultSelectedChannels: { email: sendEmail, sms: sendSms },
    }];
  }, [trial, sendEmail, sendSms]);

  const submit = async () => {
    setIsSubmitting(true);
    try {
      const res = await resendTrialConfirmation(trial.id, {
        standortId,
        channels: { email: sendEmail, sms: sendSms },
        drafts: entwurf ? { student: entwurf } : undefined,
      });
      const failed = [sendEmail && (res.email as any)?.ok === false ? 'E-Mail' : null, sendSms && (res.sms as any)?.ok === false ? 'SMS' : null].filter(Boolean);
      if (failed.length) toast.warning(`Versand fehlgeschlagen: ${failed.join(', ')}`);
      else toast.success('Bestätigung wurde erneut versendet');
      onUpdated?.(res.trial);
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e?.message || 'Versand fehlgeschlagen');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <WizardShell
      open={open}
      onOpenChange={onOpenChange}
      title="Bestätigung erneut versenden"
      description={`Schritt ${step} von 3`}
      currentStep={step}
      steps={[{ label: 'Nachricht' }, { label: 'Übersicht' }, { label: 'Senden' }]}
      onBack={step > 1 ? () => moveStep(-1) : undefined}
      canBack={step > 1 && !isSubmitting}
      onNext={step < 3 ? () => moveStep(1) : undefined}
      canNext={step < 3 ? canSubmit : false}
      onPrimary={step === 3 ? submit : undefined}
      primaryLabel={isSubmitting ? 'Sende…' : 'Senden'}
      canPrimary={step === 3 && canSubmit && !isSubmitting}
      isLoading={isSubmitting}
    >
      <div className="space-y-4 py-4 motion-safe:animate-fade-up">
        {step === 1 && composerGroups.length > 0 && standortId > 0 && (
          <div className="space-y-3">
            <CommunicationComposer
              active={open && step === 1}
              standortId={standortId}
              groups={composerGroups}
              onDraftsChange={({ groups: g }) => {
                const s = g['student'];
                if (s) {
                  setSendEmail(!!s.selectedChannels.email);
                  setSendSms(!!s.selectedChannels.sms);
                  setEntwurf({
                    subject: s.email.subjectTpl || '',
                    html: s.email.htmlTpl || '',
                    text: s.email.textTpl || '',
                    sms: s.sms.smsTpl || '',
                  });
                }
              }}
            />
          </div>
        )}
        {step === 1 && composerGroups.length > 0 && standortId <= 0 ? (
          <CommunicationStandortHint />
        ) : null}

        {step === 2 && (
          <WizardCard>
            <WizardSection title="Benachrichtigungen">
              <WizardKVGrid>
                <WizardKVItem label="Teilnehmer">{trial.studentName || '—'}</WizardKVItem>
                <WizardKVItem label="E-Mail"><WizardYesNoBadge yes={sendEmail} /></WizardKVItem>
                <WizardKVItem label="SMS"><WizardYesNoBadge yes={sendSms} /></WizardKVItem>
              </WizardKVGrid>
            </WizardSection>
          </WizardCard>
        )}

        {step === 3 && (
          <WizardCard>
            <WizardSection title="Übersicht">
              <WizardKVGrid>
                <WizardKVItem label="Teilnehmer">{trial.studentName || '—'}</WizardKVItem>
                <WizardKVItem label="E-Mail"><WizardYesNoBadge yes={sendEmail} /></WizardKVItem>
                <WizardKVItem label="SMS"><WizardYesNoBadge yes={sendSms} /></WizardKVItem>
              </WizardKVGrid>
            </WizardSection>
          </WizardCard>
        )}
      </div>
    </WizardShell>
  );
}
